import type {
  DsdEditorMappingReviewTarget,
  DsdEditorNoteReferenceCheck,
  DsdEditorValueMatch,
  DsdEditorValueOccurrence,
} from "@/apis/dsd-editor/route";
import type { DsdValueLinkVerdict } from "@/types/plain-dsd-table";
import {
  getShownUnlinkedReferences,
  valueLinkVerdictOf,
} from "./note-reference";

/** 주석 청크의 값 위치인가 — 청크 id 규칙("note…")을 아는 유일한 곳 */
export const isNoteOccurrence = (occurrence: DsdEditorValueOccurrence) =>
  occurrence.chunkId.startsWith("note");

export const getOccurrenceIds = (occurrence: DsdEditorValueOccurrence) =>
  [occurrence.id, occurrence.nodeId].filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );

const getOccurrenceKey = (
  occurrence: DsdEditorValueOccurrence,
  fallback: string,
) => getOccurrenceIds(occurrence)[0] ?? fallback;

export const getValueMatchKey = (match: DsdEditorValueMatch, index: number) =>
  getOccurrenceKey(match.source, `match-${index}`);

export const occurrenceMatchesId = (
  occurrence: DsdEditorValueOccurrence,
  selectedId: string,
) => getOccurrenceIds(occurrence).includes(selectedId);

export const valueMatchIncludesId = (
  match: DsdEditorValueMatch,
  selectedId: string,
) =>
  occurrenceMatchesId(match.source, selectedId) ||
  match.candidates.some((candidate) =>
    occurrenceMatchesId(candidate.occurrence, selectedId),
  );

export const getValueMatchCellIds = (match: DsdEditorValueMatch) => [
  ...getOccurrenceIds(match.source),
  ...match.candidates.flatMap((candidate) =>
    getOccurrenceIds(candidate.occurrence),
  ),
];

export const getSelectableOccurrenceId = (
  occurrence: DsdEditorValueOccurrence,
  renderedCellIds: ReadonlySet<string>,
) =>
  getOccurrenceIds(occurrence).find((id) => renderedCellIds.has(id)) ??
  getOccurrenceIds(occurrence)[0];

/** 같은 쌍인가 — 판단 응답과 검토 사항을 잇는 기준. 좌표 순서는 무관 */
export const sameMappingReviewPair = (
  a: { relation: string; coordinates: string[] },
  b: { relation: string; coordinates: string[] },
) =>
  a.relation === b.relation &&
  a.coordinates.length === b.coordinates.length &&
  [...a.coordinates].sort().join("\n") === [...b.coordinates].sort().join("\n");

/** 문서에 저장된 검토 판단 수. 같은 쌍이 여러 체크에 걸려도 판단 하나로 센다 */
export const countSavedMappingReviews = (
  checks: readonly { reviewTargets: DsdEditorMappingReviewTarget[] }[],
) =>
  new Set(
    checks.flatMap((check) =>
      check.reviewTargets.flatMap((target) => target.review?.id ?? []),
    ),
  ).size;

/**
 * 검토 쌍 상대 좌표(DsdEditorMappingReviewTarget.target)의 주석 청크 id — 형식 계약은 그 타입의 target 설명.
 * 셀·문단 좌표는 "{청크}:{…}", 후보가 없는 쌍은 청크 id 그대로라 첫 ':' 앞이 청크다. 이 파싱은 여기서만 한다
 */
export const getReviewTargetChunkId = (target: string) =>
  target.split(":")[0] ?? target;

/**
 * 후보(occurrence)에 붙일 검토 사항. target은 후보 셀이면 id와 같고, 문단 후보는 값 위치가
 * 아닌 문단 좌표라 id의 앞부분만 같다(note-13:sc1_pg0_p3 ↔ note-13:sc1_pg0_p3_v0).
 */
export const findMappingReviewTargetForOccurrence = (
  targets: readonly DsdEditorMappingReviewTarget[],
  occurrence: DsdEditorValueOccurrence,
) => {
  const ids = getOccurrenceIds(occurrence);
  const matches = (target: string) =>
    ids.some(
      (id) =>
        id === target ||
        id.startsWith(`${target}:`) ||
        id.startsWith(`${target}_`),
    );

  return targets.find(({ target }) => matches(target)) ?? null;
};

/**
 * 하드룰(cash_note_total 등) 연결의 양쪽 셀 → 그 셀에 걸린 서로 다른 하드룰 수. 응답에서 하드룰은
 * 연결(valueLinks)에만 실려 오므로 본문 셀(check.source)은 자기 연결의 규칙을 모아 세고, 주석 셀은 자기를
 * 이은 연결의 규칙을 센다. 문서의 빗금 셀(키)·셀 안 개수 칩(2 이상)·패널 카드의 하드룰 표기가 같은 기준을 쓴다
 */
export const getHardRuleCountByCellId = (
  checks: readonly DsdEditorNoteReferenceCheck[],
): ReadonlyMap<string, number> => {
  const rulesByCellId = new Map<string, Set<string>>();
  const add = (cellId: string, rule: string) => {
    const rules = rulesByCellId.get(cellId) ?? new Set<string>();
    rules.add(rule);
    rulesByCellId.set(cellId, rules);
  };
  for (const check of checks) {
    for (const link of check.valueLinks) {
      if (!link.hardRule) continue;
      for (const cellId of getOccurrenceIds(check.source))
        add(cellId, link.hardRule);
      add(link.cellId, link.hardRule);
    }
  }
  return new Map(
    [...rulesByCellId].map(([cellId, rules]) => [cellId, rules.size]),
  );
};

/** 다른 DSD에서 가져온 판단이 걸린 셀 — 하드룰처럼 쌍의 양쪽(본문 셀·상대). 상대가 문단·주석 청크면 표 셀이 아니라 칠할 곳이 없다 */
export const getInheritedReviewCellIds = (
  checks: readonly DsdEditorNoteReferenceCheck[],
): ReadonlySet<string> =>
  new Set(
    checks.flatMap((check) =>
      check.reviewTargets
        .filter((target) => target.review?.inheritedFromFilename)
        .flatMap((target) => [
          ...getOccurrenceIds(check.source),
          target.target,
        ]),
    ),
  );

/** 후보 목록의 카드 한 장 = 셀 하나. 값 후보와 텍스트 연결 셀이 같은 셀이면 한 장으로 합친다 */
export type DsdNoteReviewCard = {
  occurrence: DsdEditorValueOccurrence;
  /** 값 후보로 잡힌 셀 */
  fromValue: boolean;
  /** 계정과목 판정이 뽑은 셀(valueLinks[].textMatched) */
  fromText: boolean;
  /** 이 셀과의 연결 판정(valueLinkVerdictOf). 연결이 없는 후보(본문 안 후보 등)는 null */
  verdict: DsdValueLinkVerdict | null;
  /** 연결을 만든 하드룰 id. 연결이 없거나 일반 판정이면 null */
  hardRule: string | null;
  /** 검토 쌍. 초록(confirmed) 카드는 판단이 저장된 쌍만 붙인다 */
  target: DsdEditorMappingReviewTarget | null;
};

/** 셀 카드가 하나도 없는 주석의 행 — 주기에만 적힌 주석(unlinkedReferences)과의 청크 쌍 등 */
export type DsdNoteReviewChunkRow = {
  chunkId: string;
  /** 주석 제목. 없으면 참조 목록에서 찾는다 */
  title: string | null;
  caption: string | null;
  verdict: DsdValueLinkVerdict | null;
  target: DsdEditorMappingReviewTarget | null;
};

/**
 * 선택한 셀의 후보 목록. 값 후보(valueMatches)와 값 연결(valueLinks)을 셀 단위로 합친 카드와,
 * 셀 카드가 없는 주석의 행을 만든다. 카드 색은 그 셀과의 연결 근거로만 정한다 — 같은 주석의 다른 셀 근거를 섞지 않는다.
 */
export const buildNoteReviewCards = ({
  candidates,
  checks,
  pairs,
}: {
  candidates: readonly DsdEditorValueOccurrence[];
  checks: readonly DsdEditorNoteReferenceCheck[];
  pairs: readonly DsdEditorMappingReviewTarget[];
}): { cards: DsdNoteReviewCard[]; rows: DsdNoteReviewChunkRow[] } => {
  const links = checks.flatMap((check) => check.valueLinks);
  const linkOf = (occurrence: DsdEditorValueOccurrence) => {
    const ids = getOccurrenceIds(occurrence);
    return links.find((link) => ids.includes(link.cellId)) ?? null;
  };

  const cards: DsdNoteReviewCard[] = candidates.map((occurrence) => {
    const link = linkOf(occurrence);
    return {
      occurrence,
      fromValue: true,
      fromText: link?.textMatched ?? false,
      verdict: link ? valueLinkVerdictOf(link) : null,
      hardRule: link?.hardRule ?? null,
      target: null,
    };
  });
  // 값 후보 목록에 없는 연결 — 계정과목 판정이 뽑은 셀. occurrence 가 없어 후보 카드와 같은 모양으로 맞춘다
  for (const link of links) {
    if (
      cards.some((card) =>
        getOccurrenceIds(card.occurrence).includes(link.cellId),
      )
    )
      continue;
    cards.push({
      occurrence: {
        id: link.cellId,
        nodeId: link.cellId,
        chunkId: link.noteChunkId,
        chunkTitle: link.noteTitle,
        rowLabelPath: link.rowLabelPath,
        columnLabelPath: link.columnLabelPath,
        value: link.value,
        blockType: "table",
        valueType: "cell",
      },
      fromValue: link.valueMatched,
      fromText: link.textMatched,
      verdict: valueLinkVerdictOf(link),
      hardRule: link.hardRule,
      target: null,
    });
  }

  // 카드·행에 쓴 쌍. 초록 카드처럼 붙이지 않는 쌍도 넣는다 — 빠지면 마지막 "좌표만 있는 행"으로 샌다
  const usedPairs = new Set<DsdEditorMappingReviewTarget>();
  for (const card of cards) {
    const pair = findMappingReviewTargetForOccurrence(pairs, card.occurrence);
    if (pair) usedPairs.add(pair);
    // 백엔드는 초록 연결에도 쌍을 보낸다 — 검토할 게 없는 카드라 붙이지 않는다.
    // 단 이미 저장된 판단은 남긴다(근거 단위가 바뀌기 전에 검토한 쌍이 초록이 될 수 있다)
    card.target = card.verdict === "confirmed" && !pair?.review ? null : pair;
  }

  const rows: DsdNoteReviewChunkRow[] = [];
  // 주기에만 적히고 상대 셀이 없는 주석 — 노랑으로 알린다
  for (const reference of checks.flatMap(getShownUnlinkedReferences)) {
    if (!reference.chunkId) continue;
    if (rows.some((row) => row.chunkId === reference.chunkId)) continue;
    const target =
      pairs.find((pair) => pair.target === reference.chunkId) ?? null;
    if (target) usedPairs.add(target);
    rows.push({
      chunkId: reference.chunkId,
      title: reference.chunkTitle,
      caption: "주기만 있음",
      verdict: "unconfirmed",
      target,
    });
  }
  // 카드·행 어디에도 붙지 않은 쌍 — 상대 좌표만으로 보여준다
  for (const target of pairs) {
    if (usedPairs.has(target)) continue;
    rows.push({
      chunkId: target.target,
      title: null,
      caption: null,
      verdict: null,
      target,
    });
  }

  return { cards, rows };
};
